/**
 * Live specular response for the glass surfaces on the exhibition index.
 *
 * `backdrop-filter` already refracts the sandstone field behind each card, but a
 * refraction that never moves reads as a printed texture rather than as glass. Real
 * vitrine glass gives itself away through the highlight that travels across it as the
 * viewer moves. This tracks the pointer and drives that highlight.
 *
 * The work is deliberately confined to writing two custom properties per frame; all
 * the painting is CSS, so nothing here forces layout or blocks scrolling.
 */

import { motionAllowed } from "./motion";

type Tracked = { element: HTMLElement; rect: DOMRect };

export type GlassSurfaces = { dispose: () => void };

export function trackGlass(root: HTMLElement, selector: string): GlassSurfaces {
  // A pointer-driven highlight is meaningless without a pointer, and on touch the
  // finger covers the surface it would light. Motion-sensitive visitors opt out too.
  const fine = window.matchMedia("(pointer: fine)").matches;
  const still = !motionAllowed();
  if (!fine || still) return { dispose: () => undefined };

  let tracked: Tracked[] = [];
  let frame = 0;
  let pointer: { x: number; y: number } | null = null;

  const measure = (): void => {
    // Rects are cached because reading them per pointer event would thrash layout.
    tracked = [...root.querySelectorAll<HTMLElement>(selector)].map((element) => ({
      element,
      rect: element.getBoundingClientRect(),
    }));
  };

  const paint = (): void => {
    frame = 0;
    if (pointer === null) return;
    for (const { element, rect } of tracked) {
      // Position in the card's own space, clamped so a highlight parked just off
      // the edge still reads as light arriving from that side.
      const x = clamp((pointer.x - rect.left) / rect.width, -0.4, 1.4);
      const y = clamp((pointer.y - rect.top) / rect.height, -0.4, 1.4);
      element.style.setProperty("--glass-x", `${(x * 100).toFixed(1)}%`);
      element.style.setProperty("--glass-y", `${(y * 100).toFixed(1)}%`);

      // Falloff with distance, so cards far from the pointer stay quiet and the
      // surface nearest it carries the brightest reflection.
      const dx = x - 0.5;
      const dy = y - 0.5;
      const distance = Math.hypot(dx, dy);
      element.style.setProperty("--glass-gloss", clamp(1.15 - distance, 0, 1).toFixed(3));
    }
  };

  const onPointerMove = (event: PointerEvent): void => {
    pointer = { x: event.clientX, y: event.clientY };
    if (frame === 0) frame = requestAnimationFrame(paint);
  };

  const onPointerLeave = (): void => {
    pointer = null;
    for (const { element } of tracked) element.style.setProperty("--glass-gloss", "0");
  };

  const onLayoutChange = (): void => {
    measure();
    if (pointer !== null && frame === 0) frame = requestAnimationFrame(paint);
  };

  measure();
  window.addEventListener("pointermove", onPointerMove, { passive: true });
  window.addEventListener("pointerleave", onPointerLeave, { passive: true });
  window.addEventListener("resize", onLayoutChange, { passive: true });
  // The index scrolls, so cached rects go stale on every scroll tick.
  root.addEventListener("scroll", onLayoutChange, { passive: true });

  return {
    dispose: () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("resize", onLayoutChange);
      root.removeEventListener("scroll", onLayoutChange);
      tracked = [];
    },
  };
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}
